import React from 'react';
import { Typography, List, ListItem, ListItemText, Divider } from '@mui/material';
import HelpSidebar from './HelpSidebar';

interface ProfileHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export const ProfileHelpSidebar: React.FC<ProfileHelpSidebarProps> = ({
  isExpanded,
  onToggle,
}) => (
  <HelpSidebar
    isExpanded={isExpanded}
    onToggle={onToggle}
    title="Profile Help"
  >
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      Your profile shows your details, role template, times on screen, and email preferences. You can update your own date of birth and date of engagement on this page; manager and department remain read-only and can only be changed by an administrator via User Management. If your account setup is incomplete (missing company or role template), a banner explains what HR still needs to assign — the sidebar shows only Profile and Governance until setup is complete. Saving profile dates is recorded in your audit log. Personal milestones (years of service, birthday and work anniversary countdowns) appear on My Dashboard using these dates.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      TIMES ON SCREEN
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Display time zone"
          secondary="You can view times on screen in your preferred time zone — for example last signed in, when something was recorded, or when a file was added. The default is organisation time (from Settings → Country). Leave days, review deadlines, holidays, and emails are always shown in the organisation time zone. Use my device time zone to fill the list from this computer; remember to save."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      EMAIL PREFERENCES
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Five optional categories"
          secondary="You can turn on or off: Review emails; Check-in emails; IDP emails; Profile change emails; and Expense claim emails. Review cutoff reminders and peer reviewer reminders cannot be disabled. Turning off all optional emails requires confirming the acknowledgment wording. While they are off, Profile shows What you confirmed and the date (admins can edit that wording in Settings). If Settings changes the wording, you will be asked after login to reaffirm or turn emails back on. Changes are saved when you click Save preferences."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      CHECK-INS ON PROFILE
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="When your manager manages off-system"
          secondary="If your manager has turned off built-in check-ins, Profile and Check-Ins explain that they manage ongoing 1:1s outside Aardvark. You will not see a scheduled check-in frequency here in that case."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      ROLE TEMPLATE & GOVERNANCE
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Role template change"
          secondary="If an administrator has changed your role template, you will see a banner and can review and acknowledge the change here. Governance and Compliance (sidebar under Me, or the dedicated page) shows the current wording and when you last acknowledged it (set by admins in Settings)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>
  </HelpSidebar>
);

export default ProfileHelpSidebar;
